import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  TextField,
  Typography,
} from '@mui/material';
import {
  NormalizeOptionList,
  ParseNumberedMultilineOptions,
  StripNumberedOptionPrefix,
} from '../RiskAssessment_Schema';

function TodaySafetySelectModal({
  open,
  options = [],
  selectedValue = '',
  hazardSituation = '',
  title = '선택',
  manualPlaceholder = '직접 입력하세요.',
  onClose,
  onConfirm,
}) {
  const normalizedOptions = useMemo(
    () => NormalizeOptionList(options),
    [options]
  );

  const [currentValues, setCurrentValues] = useState([]);
  const [localOptions, setLocalOptions] = useState([]);
  const [manualInput, setManualInput] = useState('');

  useEffect(() => {
    const selectedItems = ParseNumberedMultilineOptions(selectedValue);
    setCurrentValues(selectedItems);
    setLocalOptions(
      NormalizeOptionList([...normalizedOptions, ...selectedItems])
    );
    setManualInput('');
  }, [selectedValue, open, normalizedOptions]);

  const IsSelected = (item) => {
    const key = StripNumberedOptionPrefix(item).toLowerCase();
    return currentValues.some(
      (v) => StripNumberedOptionPrefix(v).toLowerCase() === key
    );
  };

  const ToggleValue = (item) => {
    const v = StripNumberedOptionPrefix(item);
    if (!v) return;
    const key = v.toLowerCase();
    setCurrentValues((prev) => {
      const next = NormalizeOptionList(prev);
      const exists = next.some((x) => x.toLowerCase() === key);
      if (exists) return next.filter((x) => x.toLowerCase() !== key);
      return [...next, v];
    });
  };

  const HandleAddManual = () => {
    const v = StripNumberedOptionPrefix(manualInput);
    if (!v) return;
    setLocalOptions((prev) => NormalizeOptionList([...prev, v]));
    setCurrentValues((prev) => NormalizeOptionList([...prev, v]));
    setManualInput('');
  };

  const HandleConfirm = () => {
    onConfirm?.(NormalizeOptionList(currentValues));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            mb: 1.5,
            p: 1.25,
            borderRadius: 1,
            border: '1px solid #E5E7EB',
            bgcolor: '#F9FAFB',
          }}
        >
          <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 700 }}>
            위험발생 상황 및 결과
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.5,
            }}
          >
            {String(hazardSituation ?? '').trim() || '-'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField
            size="small"
            fullWidth
            placeholder={manualPlaceholder}
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                HandleAddManual();
              }
            }}
          />
          <Button variant="outlined" onClick={HandleAddManual}>
            직접 추가
          </Button>
        </Box>

        {localOptions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            선택 가능한 항목이 없습니다. 직접 추가로 입력해 주세요.
          </Typography>
        ) : (
          <List dense sx={{ py: 0 }}>
            {localOptions.map((item, idx) => (
              <ListItemButton
                key={`${idx}_${item}`}
                onClick={() => ToggleValue(item)}
                selected={IsSelected(item)}
                sx={{ alignItems: 'flex-start', borderRadius: 1 }}
              >
                <Checkbox
                  checked={IsSelected(item)}
                  onChange={() => ToggleValue(item)}
                  sx={{ mt: 0.35 }}
                />
                <ListItemText
                  primary={item}
                  primaryTypographyProps={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.5,
                    fontSize: '0.92rem',
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
        <Button
          variant="contained"
          onClick={HandleConfirm}
          disabled={currentValues.length === 0}
        >
          선택
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TodaySafetySelectModal;
